import { titleCase, zeroPad, ordinal } from '../../fns.js';
import { short } from '../../data/months.js';
import { short as _short } from '../../data/days.js';
import { useTitleCase } from '../../data/caseFormat.js';
import isoOffset from './_offset.js';

var applyCaseFormat = function applyCaseFormat(str) {
  if (useTitleCase()) {
    return titleCase(str);
  }

  return str;
}; // iso-year padding


var padYear = function padYear(num) {
  if (num >= 0) {
    return zeroPad(num, 4);
  } else {
    num = Math.abs(num);
    return '-' + zeroPad(num, 4);
  }
};

var format = {
  day: function day(s) {
    return applyCaseFormat(s.dayName());
  },
  'day-short': function dayShort(s) {
    return applyCaseFormat(_short()[s.day()]);
  },
  'day-number': function dayNumber(s) {
    return s.day();
  },
  'day-ordinal': function dayOrdinal(s) {
    return ordinal(s.day());
  },
  'day-pad': function dayPad(s) {
    return zeroPad(s.day());
  },
  date: function date(s) {
    return s.date();
  },
  'date-ordinal': function dateOrdinal(s) {
    return ordinal(s.date());
  },
  'date-pad': function datePad(s) {
    return zeroPad(s.date());
  },
  month: function month(s) {
    return applyCaseFormat(s.monthName());
  },
  'month-short': function monthShort(s) {
    return applyCaseFormat(short()[s.month()]);
  },
  'month-number': function monthNumber(s) {
    return s.month();
  },
  'month-ordinal': function monthOrdinal(s) {
    return ordinal(s.month());
  },
  'month-pad': function monthPad(s) {
    return zeroPad(s.month());
  },
  'iso-month': function isoMonth(s) {
    return zeroPad(s.month() + 1);
  },
  //1-based months
  year: function year(s) {
    var year = s.year();

    if (year > 0) {
      return year;
    }

    year = Math.abs(year);
    return year + ' BC';
  },
  'year-short': function yearShort(s) {
    var year = s.year();

    if (year > 0) {
      return "'".concat(String(s.year()).substr(2, 4));
    }

    year = Math.abs(year);
    return year + ' BC';
  },
  'iso-year': function isoYear(s) {
    var year = s.year();
    var isNegative = year < 0;
    var str = zeroPad(Math.abs(year), 4); //0-padded

    if (isNegative) {
      //negative years are for some reason 6-digits ('-00008')
      str = zeroPad(str, 6);
      str = '-' + str;
    }

    return str;
  },
  time: function time(s) {
    return s.time();
  },
  'time-24': function time24(s) {
    return "".concat(s.hour24(), ":").concat(zeroPad(s.minute()));
  },
  hour: function hour(s) {
    return s.hour12();
  },
  'hour-pad': function hourPad(s) {
    return zeroPad(s.hour12());
  },
  'hour-24': function hour24(s) {
    return s.hour24();
  },
  'hour-24-pad': function hour24Pad(s) {
    return zeroPad(s.hour24());
  },
  minute: function minute(s) {
    return s.minute();
  },
  'minute-pad': function minutePad(s) {
    return zeroPad(s.minute());
  },
  second: function second(s) {
    return s.second();
  },
  'second-pad': function secondPad(s) {
    return zeroPad(s.second());
  },
  millisecond: function millisecond(s) {
    return s.millisecond();
  },
  'millisecond-pad': function millisecondPad(s) {
    return zeroPad(s.millisecond(), 3);
  },
  ampm: function ampm(s) {
    return s.ampm();
  },
  AMPM: function AMPM(s) {
    return s.ampm().toUpperCase();
  },
  quarter: function quarter(s) {
    return 'Q' + s.quarter();
  },
  season: function season(s) {
    return s.season();
  },
  era: function era(s) {
    return s.era();
  },
  json: function json(s) {
    return s.json();
  },
  timezone: function timezone(s) {
    return s.timezone().name;
  },
  offset: function offset(s) {
    return isoOffset(s);
  },
  numeric: function numeric(s) {
    return "".concat(s.year(), "/").concat(zeroPad(s.month() + 1), "/").concat(zeroPad(s.date()));
  },
  // yyyy/mm/dd
  'numeric-us': function numericUs(s) {
    return "".concat(zeroPad(s.month() + 1), "/").concat(zeroPad(s.date()), "/").concat(s.year());
  },
  // mm/dd/yyyy
  'numeric-uk': function numericUk(s) {
    return "".concat(zeroPad(s.date()), "/").concat(zeroPad(s.month() + 1), "/").concat(s.year());
  },
  //dd/mm/yyyy
  'mm/dd': function mmDd(s) {
    return "".concat(zeroPad(s.month() + 1), "/").concat(zeroPad(s.date()));
  },
  //mm/dd
  // ... https://en.wikipedia.org/wiki/ISO_8601 ;(((
  iso: function iso(s) {
    var year = s.format('iso-year');
    var month = zeroPad(s.month() + 1); //1-based months

    var date = zeroPad(s.date());
    var hour = zeroPad(s.h24());
    var minute = zeroPad(s.minute());
    var second = zeroPad(s.second());
    var ms = zeroPad(s.millisecond(), 3);
    var offset = isoOffset(s);
    return "".concat(year, "-").concat(month, "-").concat(date, "T").concat(hour, ":").concat(minute, ":").concat(second, ".").concat(ms).concat(offset); //2018-03-09T08:50:00.000-05:00
  },
  'iso-short': function isoShort(s) {
    var month = zeroPad(s.month() + 1); //1-based months

    var date = zeroPad(s.date());
    var year = padYear(s.year());
    return "".concat(year, "-").concat(month, "-").concat(date); //2017-02-15
  },
  'iso-utc': function isoUtc(s) {
    return new Date(s.epoch).toISOString(); //2017-03-08T19:45:28.367Z
  },
  //i made these up
  nice: function nice(s) {
    return "".concat(short()[s.month()], " ").concat(ordinal(s.date()), ", ").concat(s.time());
  },
  'nice-24': function nice24(s) {
    return "".concat(short()[s.month()], " ").concat(ordinal(s.date()), ", ").concat(s.hour24(), ":").concat(zeroPad(s.minute()));
  },
  'nice-year': function niceYear(s) {
    return "".concat(short()[s.month()], " ").concat(ordinal(s.date()), ", ").concat(s.year());
  },
  'nice-day': function niceDay(s) {
    return "".concat(_short()[s.day()], " ").concat(applyCaseFormat(short()[s.month()]), " ").concat(ordinal(s.date()));
  },
  'nice-full': function niceFull(s) {
    return "".concat(s.dayName(), " ").concat(applyCaseFormat(s.monthName()), " ").concat(ordinal(s.date()), ", ").concat(s.time());
  },
  'nice-full-24': function niceFull24(s) {
    return "".concat(s.dayName(), " ").concat(applyCaseFormat(s.monthName()), " ").concat(ordinal(s.date()), ", ").concat(s.hour24(), ":").concat(zeroPad(s.minute()));
  }
}; //aliases

var aliases = {
  'day-name': 'day',
  'month-name': 'month',
  'iso 8601': 'iso',
  'time-h24': 'time-24',
  'time-12': 'time',
  'time-h12': 'time',
  tz: 'timezone',
  'day-num': 'day-number',
  'month-num': 'month-number',
  'month-iso': 'iso-month',
  'year-iso': 'iso-year',
  'nice-short': 'nice',
  'nice-short-24': 'nice-24',
  mdy: 'numeric-us',
  dmy: 'numeric-uk',
  ymd: 'numeric',
  'yyyy/mm/dd': 'numeric',
  'mm/dd/yyyy': 'numeric-us',
  'dd/mm/yyyy': 'numeric-us',
  'little-endian': 'numeric-uk',
  'big-endian': 'numeric',
  'day-nice': 'nice-day'
};
Object.keys(aliases).forEach(function (k) {
  return format[k] = format[aliases[k]];
});

var printFormat = function printFormat(s) {
  var str = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

  //don't print anything if it's an invalid date
  if (s.isValid() !== true) {
    return '';
  } //support .format('month')


  if (format.hasOwnProperty(str)) {
    var out = format[str](s) || '';

    if (str !== 'json') {
      out = String(out);

      if (str.toLowerCase() !== 'ampm') {
        out = applyCaseFormat(out);
      }
    }

    return out;
  } //support '{hour}:{minute}' notation


  if (str.indexOf('{') !== -1) {
    var sections = /\{(.+?)\}/g;
    str = str.replace(sections, function (_, fmt) {
      fmt = fmt.toLowerCase().trim();

      if (format.hasOwnProperty(fmt)) {
        var _out = String(format[fmt](s));

        if (fmt.toLowerCase() !== 'ampm') {
          return applyCaseFormat(_out);
        }

        return _out;
      }

      return '';
    });
    return str;
  }

  return s.format('iso-short');
};

export default printFormat;